/**
 * 2026-09-23 — the confirmation text for the member Conversation card's
 * per-channel AI pill.
 *
 * WHY THIS EXISTS. On 2026-09-18 a trainer tapped the email AI pill on a
 * member's page three times in nine seconds — on, off, on — and left it armed.
 * Nothing asked, and nothing announced it. Four days and sixteen hours later
 * the assistant answered that member's email by itself, quoting a package the
 * studio had withdrawn, and it was caught by a human reading the mailbox.
 *
 * The pill is one unconfirmed click, available to every staff member who can
 * see a member page, on roughly 2,900 members. Three taps in nine seconds is
 * the signature of a control whose state is not legible — so the fix is to
 * make the consequence legible at the moment of the click, in words, before
 * anything is written.
 *
 * Mario (2026-09-23): "let's include a confirmation so that when you click any
 * of them, a little confirmation pop-up opens up with Confirm and Cancel." So
 * BOTH directions confirm, not only arming: switching the assistant off
 * mid-conversation is also a decision a stray click should not make.
 *
 * Pure + exported so the wording is pinned by tests; `window.confirm` is the
 * page's existing idiom (`sessionNoteDeleteConfirmMessage`).
 */

/**
 * Channels where the assistant answers ONLY a member somebody switched on by
 * hand. Mirrors `MEMBER_AI_UNSET_DEFAULT_OFF_CHANNELS` in `@layer/canonical-data`
 * — duplicated here rather than imported because this file ships in the browser
 * bundle and that module reaches for `node:fs`.
 */
const OPT_IN_CHANNELS: ReadonlySet<string> = new Set(["instagram", "email"]);

export function channelAiConfirmMessage(args: {
  /** Human label for the channel, as the pill renders it (e.g. "Email"). */
  readonly label: string;
  /** Canonical channel slug (`whatsapp` / `instagram` / `email`). */
  readonly channel: string;
  /** Whether the assistant answers this person here RIGHT NOW. */
  readonly effectiveOn: boolean;
  /** Who the change applies to, when known — for an unmistakable prompt. */
  readonly memberLabel?: string | null | undefined;
}): string {
  const who = (args.memberLabel ?? "").trim();
  const target = who.length > 0 ? who : "this person";

  if (args.effectiveOn) {
    return [
      `Switch the ${args.label} assistant OFF for ${target}?`,
      "",
      `The assistant will stop replying to ${target} on ${args.label}, and a human will need to answer them. Nothing changes for anyone else, and no studio setting will switch them back on.`,
    ].join("\n");
  }

  // Arming. Name the consequence in the words that were missing on 18 Sep.
  const optIn = OPT_IN_CHANNELS.has(args.channel)
    ? ` On ${args.label} the assistant answers nobody by default — switching this on opts ${target} in individually, and it stays on until someone switches it off.`
    : "";
  return [
    `Switch the ${args.label} assistant ON for ${target}?`,
    "",
    `The assistant will reply to ${target} on ${args.label} on its own, without anyone checking first.${optIn}`,
  ].join("\n");
}
